/**
 * O quadro que renderiza HTML enviado pelo time — SEM deixá-lo tocar no GG Pilot.
 *
 * <p><b>A decisão de segurança mora aqui, e só aqui.</b> O servidor entrega HTML como
 * {@code text/plain} de propósito: se o navegador o recebesse como HTML, o documento
 * executaria na origem do GG Pilot, com a sessão de quem abriu. É este componente que
 * o devolve à vida — dentro de um iframe com {@code sandbox="allow-scripts"} e
 * <b>sem</b> {@code allow-same-origin}, porque os dois juntos anulariam o isolamento.
 * O documento roda numa origem opaca: sem cookie, sem storage, sem alcance à API.
 *
 * <p>Nasceu no visor de Documentos e saiu de lá quando as Transcrições passaram a
 * aceitar HTML (o diário de sessão): duas cópias desta regra seriam duas chances de
 * uma delas perder o sandbox numa edição distraída.
 *
 * <p>O conteúdo entra por <b>object URL</b>, não por {@code srcDoc}: com srcDoc o
 * documento herda a base do app, e um clique no índice ({@code <a href="#secao">})
 * navegava o iframe para a página do GG Pilot — que, numa origem opaca, vira tela
 * branca. E numa origem opaca o navegador recusa até a navegação de fragmento, então
 * o shim abaixo intercepta esses cliques e rola até o alvo sem navegar — a única
 * forma que o sandbox permite.
 */
import { useEffect, useState } from 'react';

const SHIM_DE_ANCORAS = `<script>
(function () {
  document.addEventListener('click', function (e) {
    var link = e.target && e.target.closest && e.target.closest('a[href^="#"]');
    if (!link) return;
    e.preventDefault();
    var id = decodeURIComponent(link.getAttribute('href').slice(1));
    if (!id) return;
    var alvo = document.getElementById(id) ||
      document.getElementsByName(id)[0] ||
      document.querySelector('a[name="' + id.replace(/"/g, '\\\\"') + '"]');
    if (alvo) alvo.scrollIntoView({ block: 'start' });
  });
})();
</script>`;

/** O shim entra antes do fechamento do body — ou no fim, se o documento não o tiver. */
function comShimDeAncoras(html: string): string {
  const fechamento = /<\/body>/i;
  return fechamento.test(html)
    ? html.replace(fechamento, `${SHIM_DE_ANCORAS}</body>`)
    : html + SHIM_DE_ANCORAS;
}

export function QuadroHtmlIsolado({
  html,
  titulo,
  className,
}: {
  html: string;
  titulo: string;
  className?: string;
}) {
  /**
   * O object URL nasce aqui e morre aqui. Guardá-lo em cache faria a segunda abertura
   * receber um endereço já revogado — e não revogá-lo faria cada documento aberto
   * ficar na memória da aba até o reload.
   */
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    const blob = new Blob([comShimDeAncoras(html)], { type: 'text/html;charset=utf-8' });
    const criada = URL.createObjectURL(blob);
    setUrl(criada);
    return () => {
      URL.revokeObjectURL(criada);
      setUrl(null);
    };
  }, [html]);

  if (!url) return null;
  return (
    <iframe
      className={className}
      title={titulo}
      // allow-scripts SEM allow-same-origin: o documento roda, numa origem opaca.
      // Os dois juntos devolveriam a ele o acesso que o sandbox tirou.
      sandbox="allow-scripts"
      src={url}
    />
  );
}
